import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { getAiConfig, saveAiConfig } from '../../utils/aiConfig';
import './AISettings.css';

const FREE_PROVIDER = { id: 'free', label: 'Free (built-in)' };

const BYOK_PROVIDERS = [
  {
    id: 'gemini',
    label: 'Google Gemini',
    keyField: 'geminiApiKey',
    keyLabel: 'Gemini API key',
    placeholder: 'AIza...',
    consoleUrl: 'https://aistudio.google.com/apikey',
    consoleLabel: 'Google AI Studio',
    billedTo: 'Google',
    free: 'Free with a Google account.',
    steps: [
      <>
        Go to Google AI Studio: navigate to{' '}
        <a href="https://aistudio.google.com" target="_blank" rel="noopener noreferrer">
          aistudio.google.com
        </a>{' '}
        and sign in with your Google account.
      </>,
      'Open the API key section: click the "Get API key" button (or go directly to the API Keys page).',
      'Create the key: click "Create API key." Choose to create it in a new Google Cloud project or attach it to an existing one.',
      "Copy and save: copy your generated API key and store it securely — you won't be able to view the secret key string again once closed.",
    ],
  },
  {
    id: 'groq',
    label: 'Groq',
    keyField: 'groqApiKey',
    keyLabel: 'Groq API key',
    placeholder: 'gsk_...',
    consoleUrl: 'https://console.groq.com/keys',
    consoleLabel: 'console.groq.com',
    billedTo: 'Groq',
    free: 'Free, ongoing — no credit card or phone number required.',
    steps: [
      <>
        Go to the Groq Console: navigate to{' '}
        <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer">
          console.groq.com
        </a>{' '}
        and sign in with Google, GitHub, or email.
      </>,
      'Open the API Keys page: click "API Keys" in the console sidebar (or go directly to console.groq.com/keys).',
      'Create the key: click "Create API Key," give it a name (e.g. "sat-prep-study-guide"), and confirm.',
      "Copy and save: copy the generated key right away and store it securely — Groq only shows it once.",
    ],
  },
];

const PROVIDERS = [FREE_PROVIDER, ...BYOK_PROVIDERS];

export default function AISettings() {
  const [config, setConfig] = useState({ provider: FREE_PROVIDER.id, groqApiKey: '', geminiApiKey: '' });
  const [status, setStatus] = useState(null); // 'testing' | 'valid' | 'invalid'
  const [statusMessage, setStatusMessage] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setConfig(getAiConfig());
  }, []);

  const active = PROVIDERS.find((p) => p.id === config.provider) || PROVIDERS[0];
  const isFree = active.id === FREE_PROVIDER.id;
  const currentKey = (!isFree && config[active.keyField]) || '';

  function handleProviderChange(id) {
    setConfig((prev) => ({ ...prev, provider: id }));
    setStatus(null);
  }

  function handleKeyChange(value) {
    setConfig((prev) => ({ ...prev, [active.keyField]: value }));
    setStatus(null);
  }

  function persist() {
    saveAiConfig(config);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function handleSave(e) {
    e.preventDefault();
    persist();
  }

  async function handleTest() {
    if (!currentKey.trim()) return;
    setStatus('testing');
    setStatusMessage('');
    try {
      await api.post('/api/ai/test-key', { provider: active.id, apiKey: currentKey.trim() });
      setStatus('valid');
    } catch (err) {
      setStatus('invalid');
      setStatusMessage(err.message || 'Could not verify this key.');
    }
  }

  return (
    <div className="ai-settings-page">
      <h1>AI Explanations</h1>
      <p className="ai-settings-intro">
        There are two options: stick with Free and use the built-in AI explanations already included with
        quizzes, practice exams, and Study Guide readings &mdash; no setup required &mdash; or bring your own
        Gemini or Groq API key to unlock &ldquo;Explain with AI&rdquo; for any selection of text in the Study
        Guide and to use your own key for every explanation across the site instead of the built-in ones. Both
        BYO providers offer a free API key with no cost to you (though you may have to be a one subscriber with
        google). Your key is stored only in this browser and is sent directly to our server with each
        explanation request; we never save it.
      </p>

      <div className="ai-settings-providers" role="radiogroup" aria-label="AI provider">
        {PROVIDERS.map((p) => (
          <label
            key={p.id}
            className={`ai-settings-provider ${config.provider === p.id ? 'ai-settings-provider--active' : ''}`}
          >
            <input
              type="radio"
              name="ai-provider"
              value={p.id}
              checked={config.provider === p.id}
              onChange={() => handleProviderChange(p.id)}
            />
            {p.label}
          </label>
        ))}
      </div>

      {isFree ? (
        <div className="ai-settings-instructions">
          <p className="ai-settings-instructions-title">
            You&rsquo;re all set <span className="ai-settings-free-badge">No key required</span>
          </p>
          <p className="ai-settings-help">
            Quizzes, practice exams, and Study Guide readings already come with built-in AI explanations at no
            cost to you. Switch to Gemini or Groq above if you&rsquo;d rather use your own key &mdash; that also
            unlocks highlighting any passage in the Study Guide to get a custom explanation for that exact
            selection.
          </p>
          <div className="ai-settings-actions">
            <button type="submit" onClick={persist}>
              Save and Use Free
            </button>
          </div>
          {saved && <p className="ai-settings-status ai-settings-status--valid">Saved.</p>}
        </div>
      ) : (
        <>
          <form onSubmit={handleSave} className="ai-settings-form">
            <label htmlFor="ai-api-key">{active.keyLabel}</label>
            <input
              id="ai-api-key"
              type="password"
              autoComplete="off"
              placeholder={active.placeholder}
              value={currentKey}
              onChange={(e) => handleKeyChange(e.target.value)}
            />
            <div className="ai-settings-actions">
              <button type="submit">Save</button>
              <button type="button" onClick={handleTest} disabled={!currentKey.trim() || status === 'testing'}>
                {status === 'testing' ? 'Testing…' : 'Test connection'}
              </button>
            </div>
          </form>

          {saved && <p className="ai-settings-status ai-settings-status--valid">Saved.</p>}
          {status === 'valid' && (
            <p className="ai-settings-status ai-settings-status--valid">Key verified — you&rsquo;re all set.</p>
          )}
          {status === 'invalid' && <p className="ai-settings-status ai-settings-status--invalid">{statusMessage}</p>}

          <div className="ai-settings-instructions">
            <p className="ai-settings-instructions-title">
              How to get a {active.label} API key <span className="ai-settings-free-badge">{active.free}</span>
            </p>
            <ol>
              {active.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            <p className="ai-settings-help">
              Or go directly to{' '}
              <a href={active.consoleUrl} target="_blank" rel="noopener noreferrer">
                {active.consoleLabel}
              </a>
              . Usage is billed to your own {active.billedTo} account (free tier applies automatically).
            </p>
          </div>
        </>
      )}
    </div>
  );
}
